/**
 * Merging two copies of the dataset. Pure, so the sync decisions are testable
 * without a network or a database.
 *
 * ## Transactions: union, last-write-wins per row
 *
 * Transactions are append-mostly and each carries a stable id derived from its
 * dedupe key, so the union of two devices is well-defined and safe: no row can
 * be lost by merging. When both sides hold the same id, the later `updatedAt`
 * wins — that is the whole reason `updatedAt` exists.
 *
 * ## Deletions: tombstones
 *
 * A union cannot express "this row was deleted" — the local copy simply lacks
 * the row, which is indistinguishable from never having seen it, so the remote
 * copy re-adds it on the next sync. Deleting a rule and watching it come back
 * is the symptom.
 *
 * So every deletion records a `Tombstone`, tombstones ride along in the synced
 * documents, and `applyTombstones` runs *after* the union to remove what was
 * deleted. A tombstone wins unconditionally: without per-row modification
 * stamps on config rows there is nothing to arbitrate a delete against a
 * concurrent edit, and honouring the delete is the choice the user can see and
 * redo, whereas ignoring it is the one that looks broken.
 *
 * Re-creating a row under the same id would be swallowed by its tombstone. For
 * config rows that is only reachable for the fixed builtin ids (`cat_*`,
 * `rule_default_*`) via re-seeding, where deleting-then-reseeding *should* stay
 * deleted; everything else there gets a fresh UUID. Transactions and statements
 * are the exception, and it is not a rare one: their ids come from the dedupe
 * key and the file hash, so re-importing a statement re-creates precisely the
 * ids an earlier deletion tombstoned. Those two collections carry a write stamp,
 * so `applyTombstones` arbitrates them instead of deleting blind.
 *
 * Tombstones are aged out after `TOMBSTONE_MAX_AGE_DAYS` — see `pruneTombstones`.
 */

import {
  TOMBSTONE_MAX_AGE_DAYS,
  type Card,
  type Category,
  type Person,
  type Rule,
  type Statement,
  type Tombstone,
  type TombstoneCollection,
  type Transaction,
} from './types.ts';

/**
 * Has the remote document changed since our last successful sync?
 *
 * `knownETag` is `null` in two situations that must not be confused: there is
 * no remote document at all, and this device has simply never synced. The
 * second one is decided by `remoteETag`, which is non-null exactly when a
 * document exists. Treating "never synced" as "unchanged" lets a caller
 * conclude the local copy is authoritative and discard a remote document it
 * has never read — which on a fresh browser means discarding everything.
 */
export function hasRemoteChanged(remoteETag: string | null, knownETag: string | null): boolean {
  if (remoteETag === null) return false;
  return remoteETag !== knownETag;
}

export interface MergeResult<T> {
  merged: T[];
  /** Rows present remotely that the local copy did not have. */
  addedFromRemote: number;
  /** Rows present locally that the remote copy did not have. */
  addedFromLocal: number;
  /** Rows held by both, where the remote copy was newer and won. */
  updatedFromRemote: number;
}

function emptyResult<T>(merged: T[]): MergeResult<T> {
  return { merged, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 };
}

/**
 * Union by id, resolving collisions with the later `updatedAt`.
 *
 * Ties (identical timestamps) keep the local copy. A tie means both sides
 * wrote in the same millisecond, which in practice means they wrote the same
 * thing; preferring local keeps the merge deterministic from each device's
 * point of view.
 */
export function mergeTransactions(
  local: readonly Transaction[],
  remote: readonly Transaction[],
): MergeResult<Transaction> {
  const byId = new Map<string, Transaction>();
  for (const row of local) byId.set(row.id, row);

  let addedFromRemote = 0;
  let updatedFromRemote = 0;

  for (const row of remote) {
    const mine = byId.get(row.id);
    if (mine === undefined) {
      byId.set(row.id, row);
      addedFromRemote += 1;
      continue;
    }
    if (row.updatedAt > mine.updatedAt) {
      byId.set(row.id, row);
      updatedFromRemote += 1;
    }
  }

  const remoteIds = new Set(remote.map((row) => row.id));
  const addedFromLocal = local.reduce(
    (count, row) => (remoteIds.has(row.id) ? count : count + 1),
    0,
  );

  return {
    merged: [...byId.values()],
    addedFromRemote,
    addedFromLocal,
    updatedFromRemote,
  };
}

/**
 * Union by id with no timestamp to arbitrate. Used for records that are
 * effectively immutable once written (statements) or that carry no per-row
 * modification stamp yet (people, cards, categories, rules). Local wins on a
 * collision, since the local copy is the one the user is looking at.
 */
export function mergeById<T extends { id: string }>(
  local: readonly T[],
  remote: readonly T[],
): MergeResult<T> {
  const byId = new Map<string, T>();
  for (const row of remote) byId.set(row.id, row);

  let addedFromLocal = 0;
  const remoteIds = new Set(byId.keys());
  for (const row of local) {
    if (!remoteIds.has(row.id)) addedFromLocal += 1;
    byId.set(row.id, row);
  }

  const localIds = new Set(local.map((row) => row.id));
  const addedFromRemote = remote.reduce(
    (count, row) => (localIds.has(row.id) ? count : count + 1),
    0,
  );

  return { merged: [...byId.values()], addedFromRemote, addedFromLocal, updatedFromRemote: 0 };
}

/** Stable ordering also resolves equal-clock conflicts consistently on both devices. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().filter((key) => record[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

export function newerRecord<T extends { updatedAt?: string }>(local: T, remote: T): T {
  const localStamp = local.updatedAt ?? '1970-01-01T00:00:00.000Z';
  const remoteStamp = remote.updatedAt ?? '1970-01-01T00:00:00.000Z';
  const normalizedLocal = { ...local, updatedAt: localStamp };
  const normalizedRemote = { ...remote, updatedAt: remoteStamp };
  if (localStamp !== remoteStamp) return localStamp > remoteStamp ? normalizedLocal : normalizedRemote;
  return canonicalJson(normalizedLocal) >= canonicalJson(normalizedRemote) ? normalizedLocal : normalizedRemote;
}

export function mergeStamped<T extends { id: string; updatedAt?: string }>(
  local: readonly T[], remote: readonly T[],
): MergeResult<T> {
  const result = mergeById(local, remote);
  const remoteById = new Map(remote.map((row) => [row.id, row]));
  result.merged = result.merged.map((row) => {
    const other = remoteById.get(row.id);
    if (!other) return { ...row, updatedAt: row.updatedAt ?? '1970-01-01T00:00:00.000Z' };
    const winner = newerRecord(row, other);
    if (canonicalJson(winner) === canonicalJson(other) && canonicalJson(row) !== canonicalJson(other)) result.updatedFromRemote++;
    return winner;
  });
  return result;
}

/* ------------------------------------------------------------- tombstones */

function tombstoneKey(tombstone: Tombstone): string {
  return `${tombstone.collection} ${tombstone.id}`;
}

/**
 * Union of two tombstone sets, keyed on collection + id.
 *
 * The *earliest* `deletedAt` wins a collision. Two devices deleting the same
 * row is not a conflict, and the earliest stamp is the one that ages out first
 * — taking the latest would let a row ping-pong between devices keep its
 * tombstone alive indefinitely.
 */
export function mergeTombstones(
  local: readonly Tombstone[],
  remote: readonly Tombstone[],
): Tombstone[] {
  const byKey = new Map<string, Tombstone>();
  for (const tombstone of [...local, ...remote]) {
    const key = tombstoneKey(tombstone);
    const existing = byKey.get(key);
    if (existing === undefined || tombstone.deletedAt < existing.deletedAt) {
      byKey.set(key, tombstone);
    }
  }
  return [...byKey.values()];
}

/**
 * Drop every row the tombstones say was deleted. Runs after the union, which
 * is the only order that works — the union is what puts them back.
 *
 * `stampOf` is what makes a deliberate re-import survive its own tombstone. An
 * imported row's id is derived from its dedupe key, not from a fresh uid, so
 * deleting a row — or undoing the import that made it — and then importing the
 * file again lands on exactly the id the tombstone names. Without a stamp to
 * arbitrate, the next sync deletes the row a second time and the import looks
 * like it silently did nothing. A row written *after* the deletion is the later
 * of the two user actions and wins, which is the same last-write-wins rule
 * `mergeTransactions` already applies.
 *
 * Collections whose rows carry no write stamp (people, cards, categories,
 * rules) pass no `stampOf` and keep deleting unconditionally — there is nothing
 * there to arbitrate with.
 */
export function applyTombstones<T extends { id: string }>(
  rows: readonly T[],
  tombstones: readonly Tombstone[],
  collection: TombstoneCollection,
  stampOf?: (row: T) => string,
): T[] {
  const deletedAtById = new Map<string, string>();
  for (const tombstone of tombstones) {
    if (tombstone.collection === collection) deletedAtById.set(tombstone.id, tombstone.deletedAt);
  }
  if (deletedAtById.size === 0) return [...rows];

  return rows.filter((row) => {
    const deletedAt = deletedAtById.get(row.id);
    if (deletedAt === undefined) return true;
    if (stampOf === undefined) return false;
    return stampOf(row) > deletedAt;
  });
}

/**
 * Forget tombstones older than the cutoff, so the synced document does not
 * accumulate every deletion ever made.
 *
 * `now` is a parameter because the domain layer never reads the clock — the
 * tests depend on that.
 */
export function pruneTombstones(
  tombstones: readonly Tombstone[],
  now: Date,
  maxAgeDays: number = TOMBSTONE_MAX_AGE_DAYS,
): Tombstone[] {
  const cutoff = new Date(now.getTime() - maxAgeDays * 24 * 60 * 60 * 1000).toISOString();
  return tombstones.filter((tombstone) => tombstone.deletedAt >= cutoff);
}

export interface ConfigParts {
  people: Person[];
  cards: Card[];
  categories: Category[];
  rules: Rule[];
}

export interface ConfigMergeResult {
  merged: ConfigParts;
  /**
   * True when both copies changed since the last sync. The merge itself is
   * still safe (it is additive), but the user is told rather than left to
   * discover it.
   */
  bothChanged: boolean;
  addedFromRemote: number;
}

export function mergeConfig(
  local: ConfigParts,
  remote: ConfigParts,
  bothChanged: boolean,
): ConfigMergeResult {
  const people = mergeById(local.people, remote.people);
  const cards = mergeById(local.cards, remote.cards);
  const categories = mergeById(local.categories, remote.categories);
  const rules = mergeById(local.rules, remote.rules);

  return {
    merged: {
      people: people.merged,
      cards: cards.merged,
      categories: categories.merged,
      rules: rules.merged,
    },
    bothChanged,
    addedFromRemote:
      people.addedFromRemote +
      cards.addedFromRemote +
      categories.addedFromRemote +
      rules.addedFromRemote,
  };
}

export function mergeStatements(
  local: readonly Statement[],
  remote: readonly Statement[],
): MergeResult<Statement> {
  if (remote.length === 0) return emptyResult([...local]);
  return mergeById(local, remote);
}
